import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * R6 A11 — Right-click / long-press row context menu.
 *
 * Final action set:
 *   View
 *   Edit
 *   Add Activity
 *   [ Add Next Contact ]  OR  [ Mark Next Complete + Suspend Next ]
 *   ───
 *   Edit Products
 *   Edit Status
 *   Edit Source
 *   Assign to Rep          (managers only)
 *   ───
 *   Delete Lead            (managers only)
 */
export default function RowContextMenu({
  open,
  x,
  y,
  lead,
  onClose,
  onAction,
}) {
  const ref = useRef(null);
  const [position, setPosition] = useState({ x, y });

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("contextmenu", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("contextmenu", close);
    };
  }, [open, onClose]);

  useLayoutEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      const menu = ref.current;
      if (!menu) return;

      const rect = menu.getBoundingClientRect();
      const GAP = 8;

      let nextX = x;
      let nextY = y;

      // Keep the menu horizontally inside the viewport.
      if (nextX + rect.width > window.innerWidth - GAP) {
        nextX = window.innerWidth - rect.width - GAP;
      }

      if (nextX < GAP) {
        nextX = GAP;
      }

      // If there isn't enough room below the button,
      // open the menu upward instead.
      if (nextY + rect.height > window.innerHeight - GAP) {
        nextY = y - rect.height - 12;
      }

      // Final vertical safety clamp.
      if (nextY < GAP) {
        nextY = GAP;
      }

      setPosition({
        x: nextX,
        y: nextY,
      });
    };

    updatePosition();

    const frame = requestAnimationFrame(updatePosition);

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, x, y]);

  if (!open || !lead) return null;

  const item = (key, label, icon, danger = false) => (
    <button
      key={key}
      type="button"
      className={`r6-row-menu-item ${danger ? "is-danger" : ""}`}
      onClick={() => onAction?.(key, lead)}
    >
      <i className={`fas ${icon}`} />
      <span>{label}</span>
    </button>
  );

  const isManager = ["Admin", "SM", "Sales Owner", "Sales Manager"].includes(
    String(lead.__userRole || "").trim(),
  );

  // Mirror inline NextActivityCell: only Active/Overdue count as "existing"
  const activeNext = lead.active_next_activity;
  const hasActiveNext =
    activeNext && ["Active", "Overdue"].includes(activeNext.status);

  return (
    <div
      ref={ref}
      className="r6-row-context-menu"
      style={{
        position: "fixed",
        top: position.y,
        left: position.x,
        zIndex: 3000,
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {item("open", "View", "fa-eye")}
      {item("edit", "Edit", "fa-edit")}
      {item("activity", "Add Activity", "fa-plus-circle")}

      {/* Next Contact slot — one of two states */}
      {hasActiveNext
        ? item("nextComplete", "Mark Next Contact Complete", "fa-check")
        : item("next", "Add Next Contact", "fa-calendar-plus")}

      {hasActiveNext && item("nextSuspend", "Suspend Next Contact", "fa-pause")}

      <div className="r6-row-menu-divider" />

      {item("products", "Edit Products", "fa-box-open")}
      {item("status", "Edit Status", "fa-exchange-alt")}
      {item("source", "Edit Source", "fa-random")}
      {isManager && item("assign", "Assign to Rep", "fa-user-tag")}

      <div className="r6-row-menu-divider" />

      {isManager && item("delete", "Delete Lead", "fa-trash", true)}
    </div>
  );
}
